<template>
  <div class="Home">
    <Header overlay />

    <HomepageHero />

    <HomepageIntro />

    <SectionDivider from="#001116" to="#ffffff" />

    <div id="features" class="features-section lg:py-24 py-16">
      <FeaturesDashboard />
    </div>

    <SectionDivider from="#F9FBFD" to="#001116" />

    <TrainingPlansPromo />

    <AiCyclingCoach />

    <FeaturesAnalytics />

    <div class="md:py-16 md:pt-32 py-6 mt-8 bg-white">
      <FeaturesMetrics
        class="max-w-page w-full px-8 mx-auto"
        subtitle="Critical Power, W', Normalized Power, aerobic efficiency - FormBeat shows you the ones that actually explain your training."
      />
    </div>

    <SectionDivider from="#ffffff" to="#001116" />

    <Testimonials />

    <div class="bg-deepblue pb-16 font-bold text-center">
      <router-link :to="{ name: 'join' }" class="sm:w-auto w-auto">
        <AppButton primary>Get Started</AppButton>
      </router-link>
      <p class="max-w-md mx-auto mt-5 text-sm font-normal text-gray-400">
        Link Strava or Garmin once. Every ride you've done flows in - and every ride after syncs itself.
      </p>
    </div>

    <SectionDivider from="#001116" to="#f7fafc" />

    <div id="faq" class="py-16 bg-gray-100">
      <Faq class="max-w-page w-full px-8 mx-auto" />
    </div>

    <div class="sm:px-8 px-4 pb-16 bg-gray-100">
      <ClosingCta />

      <p class="text-muted mt-8 text-lg text-center">
        Do other riders follow your training? Join the
        <router-link :to="{ name: 'ambassadors' }" class="underline" title="FormBeat Ambassador Program">Ambassador Program</router-link>
        and get Premium free.
      </p>
    </div>

    <Footer />
  </div>
</template>

<script>
import Header from 'components/Header'
import Footer from 'components/Footer'

import HomepageHero from 'components/HomepageHero'
import HomepageIntro from 'components/HomepageIntro'
import TrainingPlansPromo from 'components/TrainingPlansPromo'
import FeaturesDashboard from 'components/FeaturesDashboard'
import FeaturesMetrics from 'components/FeaturesMetrics'
import FeaturesAnalytics from 'components/FeaturesAnalytics'
import Testimonials from 'components/Testimonials'
import Faq from 'components/Faq'
import AiCyclingCoach from 'components/AiCyclingCoach'
import SectionDivider from 'components/SectionDivider'
import ClosingCta from 'components/ClosingCta'
import { homeFaqs } from 'data/homeFaqs'

export default {
  components: {
    Header,
    Footer,

    HomepageHero,
    HomepageIntro,
    TrainingPlansPromo,
    FeaturesDashboard,
    FeaturesMetrics,
    FeaturesAnalytics,
    Testimonials,
    Faq,
    AiCyclingCoach,
    SectionDivider,
    ClosingCta
  },

  watch: {
    '$route.query'() {
      this.scrollToSection()
    }
  },

  mounted() {
    setTimeout(() => {
      this.scrollToSection()
    }, 0)
  },

  methods: {
    scrollToSection() {
      if (!this.$route.query.section) {
        return
      }

      if (['features', 'pricing'].includes(this.$route.query.section)) {
        document.querySelector(`#${this.$route.query.section}`)?.scrollIntoView({
          behavior: 'smooth'
        })
      }
    }
  },

  metaInfo() {
    return {
      title: 'AI Cycling Analytics & Training Dashboard',
      titleTemplate: '%s',
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content:
            'AI-powered cycling analytics designed to help cyclists of any fitness level focus on productive and sustainable training. Improve your performance and cycling form with our science based tools and metrics.'
        }
      ],
      script: [
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': homeFaqs.map((faq) => ({
              '@type': 'Question',
              'name': faq.title,
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': faq.content
                  .replace(/<[^>]+>/g, ' ')
                  .replace(/\s+/g, ' ')
                  .replace(/\s+([.,])/g, '$1')
                  .trim()
              }
            }))
          }
        },
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            'name': 'Formbeat',
            'url': 'https://www.formbeat.com',
            'logo': 'https://www.formbeat.com/images/logo.png',
            'description':
              'AI-powered cycling analytics platform designed to help cyclists of any fitness level focus on productive and sustainable training. Integrate with popular cycling platforms for comprehensive performance analysis.',
            'foundingDate': '2019',
            'contactPoint': {
              '@type': 'ContactPoint',
              'contactType': 'customer support',
              'email': 'support@formbeat.com'
            },
            'sameAs': ['https://apps.apple.com/us/app/formbeat/id1539444595', 'https://play.google.com/store/apps/details?id=com.formbeat.www'],
            'offers': {
              '@type': 'Offer',
              'category': 'SaaS',
              'description': 'Cycling performance analytics and training insights'
            },
            'applicationCategory': 'HealthApplication',
            'operatingSystem': 'Web, iOS, Android',
            'featureList': [
              'AI coach',
              'Cycling platform integration',
              'Power curve analysis',
              'Training load optimization',
              'Aerobic efficiency tracking',
              'Weekly summary emails',
              'Activity analysis and insights',
              'Performance tracking',
              'Training zone calculators'
            ],
            'audience': {
              '@type': 'Audience',
              'audienceType': 'intermediate cyclists, cycling enthusiasts, power meter users, data-driven athletes'
            },
            'parentOrganization': {
              '@type': 'Organization',
              'name': 'Formbeat Ltd'
            }
          }
        },
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            'name': 'Formbeat - AI Cycling Analytics Platform',
            'url': 'https://www.formbeat.com',
            'description':
              'AI-powered cycling analytics platform that integrates with popular cycling platforms to provide meaningful training insights for intermediate cyclists. Features AI coach, power curve analysis, and weekly training summaries.',
            'applicationCategory': 'HealthApplication',
            'operatingSystem': 'Web Browser, iOS, Android',
            'offers': [
              {
                '@type': 'Offer',
                'name': 'Free Trial Plan',
                'price': '0',
                'priceCurrency': 'USD',
                'description': 'Basic analytics and tools'
              },
              {
                '@type': 'Offer',
                'name': 'Premium Plan',
                'description': 'Full analytics suite with AI insights'
              }
            ],
            'provider': {
              '@type': 'Organization',
              'name': 'Formbeat',
              'url': 'https://www.formbeat.com'
            },
            'keywords':
              'cycling analytics, AI cycling coach, cycling data analysis, platform integration, AI coach, power curve analysis, training load, cycling performance',
            'audience': {
              '@type': 'Audience',
              'audienceType': 'intermediate cyclists seeking actionable training insights'
            },
            'aggregateRating': {
              '@type': 'AggregateRating',
              'ratingValue': '4.8',
              'reviewCount': '24',
              'description': 'Highly rated cycling analytics platform for intermediate cyclists'
            }
          }
        }
      ]
    }
  }
}
</script>

<style lang="postcss" scoped>
.Home {
  @apply w-full bg-white;
}

.features-section {
  background: white linear-gradient(to top, #f7fafc 0%, #fff 400px) no-repeat;
}
</style>
